import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { RevealApi } from 'reveal.js';
import styles from './AudioVisualizer.module.css';

// Add these constants at the top of the file, after the imports
const VISUALIZER_CONFIG = {
  MIC_SENSITIVITY: 3.0, // Increase for more mic sensitivity
  AUDIO_SENSITIVITY: 1.0, // Audio playback sensitivity
  FFT_SIZE: 256, // Frequency sample size
  SMOOTHING: 0.8, // Smoothing time constant
};

interface AudioVisualizerProps {
  className?: string;
  deck: RevealApi;
}

type VisualizerMode = 'full' | 'bottom-right' | 'hidden';

// The visualizer layout mode is fully derived from the reveal.js deck's
// current slide, so it is read straight from that external system via
// useSyncExternalStore instead of being duplicated into local state.
const useVisualizerMode = (deck: RevealApi): VisualizerMode => {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!deck) return () => {};
      deck.on('slidechanged', callback);
      return () => {
        deck.off('slidechanged', callback);
      };
    },
    [deck]
  );

  const getSnapshot = useCallback((): VisualizerMode => {
    const visualizerMode = deck?.getCurrentSlide()?.dataset.visualizer;
    return (visualizerMode as VisualizerMode) || 'hidden';
  }, [deck]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

export const AudioVisualizer = ({ deck, className = '' }: AudioVisualizerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mode = useVisualizerMode(deck);
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const analyserRef = useRef<AnalyserNode | undefined>(undefined);
  const sourceRef = useRef<MediaElementAudioSourceNode | undefined>(undefined);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const audioElementRef = useRef<HTMLAudioElement | undefined>(undefined);
  const micAnalyserRef = useRef<AnalyserNode | undefined>(undefined);
  const micStreamRef = useRef<MediaStream | undefined>(undefined);
  const [hasMicAccess, setHasMicAccess] = useState<boolean>(false);
  const micAudioContextRef = useRef<AudioContext | undefined>(undefined);

  const drawWaveform = (
    ctx: CanvasRenderingContext2D,
    dataArray: Uint8Array,
    width: number,
    height: number,
    sensitivity: number,
    color: string,
    time: number
  ) => {
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 4;

    // Remove shadow blur for cleaner look
    ctx.shadowBlur = 0;
    ctx.lineWidth = 1.5;

    // Draw multiple rotating circles
    for (let ring = 0; ring < 3; ring++) {
      const ringRadius = radius * (0.6 + ring * 0.2);
      const rotationOffset = time * 0.001 * (ring + 1) + (ring * Math.PI) / 3;

      ctx.beginPath();
      for (let i = 0; i < dataArray.length; i++) {
        const angle = (i / dataArray.length) * Math.PI * 2 + rotationOffset;
        const value = (dataArray[i] / 128.0 - 1) * sensitivity;

        // Create dynamic radius with multiple wave influences
        const dynamicRadius =
          ringRadius + value * radius * 0.3 + Math.sin(time * 0.002 + angle * 3) * 10 * (ring + 1);

        const x = centerX + Math.cos(angle) * dynamicRadius;
        const y = centerY + Math.sin(angle) * dynamicRadius;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.closePath();
      ctx.strokeStyle = color;
      ctx.stroke();

      // Add connecting lines between rings with reduced opacity
      if (ring > 0) {
        for (let i = 0; i < 12; i++) {
          const angle = (i / 12) * Math.PI * 2 + rotationOffset;

          const innerRadius = radius * (0.4 + (ring - 1) * 0.2);
          const outerRadius = ringRadius;

          const x1 = centerX + Math.cos(angle) * innerRadius;
          const y1 = centerY + Math.sin(angle) * innerRadius;
          const x2 = centerX + Math.cos(angle) * outerRadius;
          const y2 = centerY + Math.sin(angle) * outerRadius;

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.15;
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !deck) {
      console.log('[AudioVisualizer] No canvas or deck', { canvas: !!canvas, deck: !!deck });
      return;
    }

    // Initialize canvas context and handle resize
    const ctx = canvas.getContext('2d')!;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      console.log('[AudioVisualizer] Canvas resized', {
        width: canvas.width,
        height: canvas.height,
      });
    };

    resize();
    window.addEventListener('resize', resize);

    // Initialize microphone separately from audio handling
    const initMicrophone = async () => {
      try {
        console.log('[AudioVisualizer] Initializing microphone');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = stream;

        // Store the mic audio context in the ref
        const AudioContextConstructor =
          window.AudioContext ||
          (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextConstructor) {
          throw new Error('AudioContext is not supported in this browser');
        }
        micAudioContextRef.current = new AudioContextConstructor();
        micAnalyserRef.current = micAudioContextRef.current.createAnalyser();
        micAnalyserRef.current.fftSize = VISUALIZER_CONFIG.FFT_SIZE;
        micAnalyserRef.current.smoothingTimeConstant = VISUALIZER_CONFIG.SMOOTHING;

        const micSource = micAudioContextRef.current.createMediaStreamSource(stream);
        micSource.connect(micAnalyserRef.current);

        setHasMicAccess(true);
        console.log('[AudioVisualizer] Microphone initialized successfully');
      } catch (error) {
        console.log('[AudioVisualizer] Microphone initialization failed:', error);
        setHasMicAccess(false);
      }
    };

    const draw = () => {
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const time = performance.now();

      // Only draw microphone visualization if we have access
      if (hasMicAccess && micAnalyserRef.current) {
        const bufferLength = micAnalyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        micAnalyserRef.current.getByteTimeDomainData(dataArray);

        drawWaveform(
          ctx,
          dataArray,
          canvas.width,
          canvas.height,
          VISUALIZER_CONFIG.MIC_SENSITIVITY,
          'rgba(255, 0, 0, 0.4)',
          time + 1000
        );
      }

      // Draw audio visualization as normal
      if (analyserRef.current) {
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteTimeDomainData(dataArray);

        drawWaveform(
          ctx,
          dataArray,
          canvas.width,
          canvas.height,
          VISUALIZER_CONFIG.AUDIO_SENSITIVITY,
          'rgba(0, 255, 255, 0.9)',
          time
        );
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    // Start drawing immediately, don't wait for mic initialization
    draw();

    // Try to initialize microphone, but don't block on it
    initMicrophone();

    // Initialize audio context
    const initAudio = () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      const AudioContextConstructor =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextConstructor) {
        console.error('[AudioVisualizer] AudioContext is not supported in this browser');
        return;
      }
      audioContextRef.current = new AudioContextConstructor();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = VISUALIZER_CONFIG.FFT_SIZE;
      analyserRef.current.smoothingTimeConstant = VISUALIZER_CONFIG.SMOOTHING;
    };

    const stopAudio = () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.currentTime = 0;
      }
    };

    const playAudio = async (src?: string) => {
      try {
        stopAudio();
        initAudio();

        if (src) {
          if (!audioElementRef.current || audioElementRef.current.src !== src) {
            if (sourceRef.current) {
              sourceRef.current.disconnect();
              sourceRef.current = undefined;
            }

            audioElementRef.current = new Audio(src);

            if (!audioContextRef.current || !analyserRef.current) {
              console.log('[AudioVisualizer] Audio context not ready');
              return;
            }

            sourceRef.current = audioContextRef.current.createMediaElementSource(
              audioElementRef.current
            );
            sourceRef.current.connect(analyserRef.current);
            analyserRef.current.connect(audioContextRef.current.destination);
          }

          await audioElementRef.current.play().catch((error) => {
            console.warn('[AudioVisualizer] Audio playback failed:', error.message);
          });
        }
      } catch (error) {
        console.error('[AudioVisualizer] Audio error:', error);
      }
    };

    const handleSlideChange = (event: any) => {
      stopAudio();
      const audioSrc = event.currentSlide.dataset.audio;
      playAudio(audioSrc);
    };

    deck.on('slidechanged', handleSlideChange);

    // Check initial slide. This only triggers the audio side effect - the
    // visualizer `mode` itself is derived separately via useSyncExternalStore.
    const currentSlide = deck.getCurrentSlide();
    if (currentSlide) {
      playAudio(currentSlide.dataset.audio);
    }

    // Cleanup
    return () => {
      deck.off('slidechanged', handleSlideChange);
      stopAudio();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }
      if (micAudioContextRef.current?.state !== 'closed') {
        micAudioContextRef.current?.close();
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      window.removeEventListener('resize', resize);
    };
  }, [deck, hasMicAccess]);

  return <canvas ref={canvasRef} className={`${styles.canvas} ${styles[mode]} ${className}`} />;
};
